/**
 * @fileoverview Attaches an already-sanitised fragment to the document.
 *
 * WHY A FRAGMENT AND NOT `dangerouslySetInnerHTML`. The renderer sanitises, then
 * walks the resulting nodes to resolve `nostr:` links and anchor headings.
 * Serialising that back to a string for React to re-parse would throw the work
 * away and give a second, unsanitised-looking parse -- and `dangerouslySet-`
 * would be the only sanitiser-shaped word left in the file, in a component that
 * is not the one doing the sanitising. Handing over nodes keeps "sanitised" a
 * property of the value rather than a claim in a comment.
 *
 * In-app links are intercepted so a `nostr:` reference to another page routes
 * without a full reload. Anything else -- an off-site link, a modified click --
 * is left to the browser.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';


/**
 * A copy button on each code block. OURS, added after sanitising, and never
 * part of page content: the sanitiser forbids `<button>` from a page, which is
 * right, and this control is the reader's rather than the author's.
 */
function addCopyButtons(root: HTMLElement): void {
  for (const pre of Array.from(root.querySelectorAll<HTMLElement>('pre.pages-code'))) {
    if (pre.parentElement?.classList.contains('pages-code-wrap')) continue;
    const wrap = document.createElement('div');
    wrap.className = 'pages-code-wrap';
    pre.replaceWith(wrap);
    wrap.appendChild(pre);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pages-copy';
    button.textContent = 'Copy';
    button.setAttribute('aria-label', 'Copy code');
    wrap.appendChild(button);
  }
}

export function PageBody({ fragment }: { fragment: DocumentFragment }) {
  const host = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    el.replaceChildren(fragment.cloneNode(true));
    addCopyButtons(el);
    // DIAGRAMS ARE NOT DRAWN, and the drawing module is not imported, so the
    // library is never loaded. Drawing let a page's author make every reader
    // fetch a URL of their choosing (cloistr-pr on MR 78); it returns once that
    // is closed. A diagram reads as its source until then.
  }, [fragment]);

  useEffect(() => {
    const el = host.current;
    if (!el) return;

    function onClick(event: MouseEvent) {
      const copy = (event.target as Element | null)?.closest?.('button.pages-copy');
      if (copy) {
        const code = copy.parentElement?.querySelector('pre code')?.textContent ?? '';
        void navigator.clipboard?.writeText(code).then(
          () => {
            copy.textContent = 'Copied';
            setTimeout(() => (copy.textContent = 'Copy'), 1500);
          },
          () => {
            copy.textContent = 'Copy failed';
          }
        );
        return;
      }
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as Element | null)?.closest?.('a[data-page-link="true"]');
      const href = anchor?.getAttribute('href');
      if (!href || !href.startsWith('/')) return;

      event.preventDefault();
      // react-router v7's navigate returns a promise nobody needs to await.
      void navigate(href);
    }

    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [navigate]);

  return <div ref={host} className="page-body" />;
}
